/* Backup & DR — handover §14 step 11, and the section that justifies shipping
   this as a PWA at all.

   §2: this app documents the recovery of the infrastructure it is hosted on. If
   Hull is gone, an SPFx-hosted runbook is gone with it. So recovery plans are
   among the four things pinned for offline, and the steps are on the device
   before anybody needs them.

   The number that matters here is not "is there a backup" — everybody has
   backups. It is **when was it last proved**, because an untested backup is a
   hope, not a plan. So the screen leads on tested-ness rather than on
   scheduled-ness, and a job that has never been restored from reads as red
   however green its last run was. */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { daysSince, fmtDate } from '../../lib/format';
import type { IBackup, IRecoveryPlan, IRestoreTest } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  LoadError,
  Panel,
  StatCard,
  StatGrid,
  TableSkeleton,
} from '../ui';
import { EditDrawer } from './EditDrawer';
import { backupFields, valuesSeen } from '../../lib/registerFields';
import s from './screens.module.scss';

/** How stale the last proven restore is. A year is the point at which "we
    tested it" stops meaning anything about the system as it is now. */
const STALE_TEST_DAYS = 365;

export function testTone(job: Pick<IBackup, 'tested' | 'testOk'>): 'ok' | 'warn' | 'bad' {
  if (!job.tested || job.tested === '—') return 'bad';
  if (job.testOk === false) return 'bad';
  return daysSince(job.tested) > STALE_TEST_DAYS ? 'warn' : 'ok';
}

export function BackupDrScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();

  const [job, setJob] = useState<IBackup | null>(null);
  const [addingJob, setAddingJob] = useState(false);
  const [plan, setPlan] = useState<IRecoveryPlan | null>(null);
  const [planDraft, setPlanDraft] = useState<IRecoveryPlan | null>(null);
  const [logging, setLogging] = useState<IBackup | null>(null);
  const [test, setTest] = useState<IRestoreTest | null>(null);

  const jobs = useQuery({ queryKey: qk.backups, queryFn: () => svc.backups.jobs() });
  const plans = useQuery({ queryKey: qk.plans, queryFn: () => svc.backups.plans() });
  const tests = useQuery({ queryKey: qk.tests, queryFn: () => svc.backups.tests() });
  /* The two registers a job can point at, so what it covers is ticked rather
     than described. */
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const apps = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.backups });
    void qc.invalidateQueries({ queryKey: qk.plans });
    void qc.invalidateQueries({ queryKey: qk.tests });
    void qc.invalidateQueries({ queryKey: qk.audit });
    setJob(null);
    setAddingJob(false);
    setPlanDraft(null);
    setLogging(null);
    setTest(null);
  };

  const saveJob = useMutation({

    meta: { ok: 'Backup job saved' },
    mutationFn: (values: Record<string, unknown>) =>
      svc.backups.saveJob({
        ...(job ?? ({ id: '' } as IBackup)),
        ...(values as Partial<IBackup>),
        crit: Number(values.crit ?? 3) as IBackup['crit'],
      } as IBackup),
    onSuccess: refresh,
  });

  const removeJob = useMutation({

    meta: { ok: 'Backup job deleted' },
    mutationFn: (id: string) => svc.backups.removeJob(id),
    onSuccess: refresh,
  });

  const savePlan = useMutation({

    meta: { ok: 'Recovery plan saved' },
    mutationFn: (p: IRecoveryPlan) => svc.backups.savePlan(p),
    onSuccess: refresh,
  });

  const logTest = useMutation({

    meta: { ok: 'Restore test logged' },
    mutationFn: (t: IRestoreTest) => svc.backups.logTest(t),
    onSuccess: refresh,
  });

  const stats = useMemo(() => {
    const all = jobs.data ?? [];
    return {
      total: all.length,
      untested: all.filter((j) => !j.tested || j.tested === '—').length,
      stale: all.filter((j) => testTone(j) === 'warn').length,
      failing: all.filter((j) => j.ok === false).length,
    };
  }, [jobs.data]);

  const blankPlan = (): IRecoveryPlan => ({
    id: '',
    name: '',
    trigger: '',
    rto: '',
    last: '—',
    tier: 1,
    steps: [''],
  });

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Backup &amp; DR</h1>
          <p>
            What is backed up, when it was last <em>proved</em>, and what to do when it
            matters.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => { setJob(null); setAddingJob(true); }} disabled={!online}>
          New backup job
        </Button>
      </div>

      {(jobs.isError || plans.isError) && (
        <LoadError error={jobs.error ?? plans.error} what="backup and recovery" />
      )}

      <StatGrid>
        <StatCard
          icon="ti-shield-bolt"
          label="Backup jobs"
          value={jobs.isLoading ? '—' : stats.total}
          tone="info"
          foot={`${plans.data?.length ?? 0} recovery plans`}
        />
        <StatCard
          icon="ti-flask-off"
          label="Never restored from"
          value={jobs.isLoading ? '—' : stats.untested}
          tone={stats.untested > 0 ? 'bad' : 'ok'}
          foot={stats.untested > 0 ? 'These are hopes, not plans' : 'All proven at least once'}
        />
        <StatCard
          icon="ti-clock-exclamation"
          label="Test over a year old"
          value={jobs.isLoading ? '—' : stats.stale}
          tone={stats.stale > 0 ? 'warn' : 'ok'}
          foot="The system has moved on since"
        />
        <StatCard
          icon="ti-alert-triangle"
          label="Last run failed"
          value={jobs.isLoading ? '—' : stats.failing}
          tone={stats.failing > 0 ? 'bad' : 'ok'}
          foot="Pushed in by the backup software"
        />
      </StatGrid>

      <div className={s.stack}>
        <Panel title="Backup jobs" sub="Sorted by last proven restore" flush>
          {jobs.isLoading ? (
            <TableSkeleton />
          ) : (jobs.data ?? []).length === 0 ? (
            <EmptyState icon="ti-shield-bolt" title="Nothing recorded">
              Add a job to record what it covers, where it lands and when it was last proved.
            </EmptyState>
          ) : (
            <div className={s.rows}>
              {[...(jobs.data ?? [])]
                .sort((a, b) => {
                  const rank = { bad: 0, warn: 1, ok: 2 };
                  return rank[testTone(a)] - rank[testTone(b)];
                })
                .map((j) => (
                  <div key={j.id} className={s.row}>
                    <span className={s.rowIcon}>
                      <i className="ti ti-database" aria-hidden />
                    </span>
                    <button
                      type="button"
                      className={`${s.rowMain} ${s.rowButton}`}
                      onClick={() => setJob(j)}
                    >
                      <div className={s.rowTitle}>{j.name}</div>
                      <div className={s.rowSub}>
                        {j.what} · {j.sched} · keeps {j.ret}
                      </div>
                    </button>
                    {j.offsite && <Badge tone="ok">Offsite</Badge>}
                    {j.immutable && <Badge tone="ok">Immutable</Badge>}
                    <Badge tone={testTone(j)}>
                      {!j.tested || j.tested === '—'
                        ? 'Never tested'
                        : `Tested ${fmtDate(j.tested)}`}
                    </Badge>
                    <Button
                      icon="ti-flask"
                      disabled={!online}
                      onClick={() => {
                        setLogging(j);
                        setTest({
                          when: new Date().toISOString().slice(0, 10),
                          what: j.name,
                          who: '',
                          mins: 0,
                          ok: true,
                          note: '',
                        });
                      }}
                    >
                      Log test
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Recovery plans"
          sub="Pinned for offline"
          flush
          actions={
            <Button icon="ti-plus" disabled={!online} onClick={() => setPlanDraft(blankPlan())}>
              New plan
            </Button>
          }
        >
          {plans.isLoading ? (
            <TableSkeleton rows={4} />
          ) : (plans.data ?? []).length === 0 ? (
            <EmptyState icon="ti-route" title="No recovery plans">
              A plan says who does what, in what order, to get a service back.
            </EmptyState>
          ) : (
            <div className={s.rows}>
              {[...(plans.data ?? [])]
                .sort((a, b) => b.tier - a.tier)
                .map((p) => (
                  <div key={p.id} className={s.row}>
                    <span className={s.rowIcon}>
                      <i className="ti ti-route" aria-hidden />
                    </span>
                    <button
                      type="button"
                      className={`${s.rowMain} ${s.rowButton}`}
                      onClick={() => setPlan(p)}
                    >
                      <div className={s.rowTitle}>{p.name}</div>
                      <div className={s.rowSub}>
                        {p.steps.length} steps · target {p.rto || '—'}
                      </div>
                    </button>
                    <Badge tone={p.tier >= 3 ? 'bad' : p.tier === 2 ? 'warn' : 'muted'}>
                      Tier {p.tier}
                    </Badge>
                    <Badge tone={p.last && p.last !== '—' ? 'ok' : 'warn'}>
                      {p.last && p.last !== '—' ? `Rehearsed ${fmtDate(p.last)}` : 'Never rehearsed'}
                    </Badge>
                  </div>
                ))}
            </div>
          )}
        </Panel>

        <Panel title="Restore tests" sub="Proof that the backups restore" flush>
          {tests.isLoading ? (
            <TableSkeleton rows={3} />
          ) : (tests.data ?? []).length === 0 ? (
            <EmptyState icon="ti-flask" title="No restore tests logged" />
          ) : (
            <div className={s.rows}>
              {(tests.data ?? []).map((t, i) => (
                <div key={`${t.when}-${i}`} className={s.row}>
                  <span className={s.rowIcon}>
                    <i className={`ti ${t.ok ? 'ti-check' : 'ti-x'}`} aria-hidden />
                  </span>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{t.what}</div>
                    <div className={s.rowSub}>
                      {t.who || '—'} · {t.mins} minutes{t.note ? ` · ${t.note}` : ''}
                    </div>
                  </div>
                  <Badge tone={t.ok ? 'ok' : 'bad'}>{t.ok ? 'Restored' : 'Failed'}</Badge>
                  <div className={s.rowWhen}>{fmtDate(t.when)}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <EditDrawer
        open={job !== null || addingJob}
        record={job ? { ...job, crit: String(job.crit) } : null}
        fields={backupFields(valuesSeen(jobs.data, ['product', 'ret', 'dest', 'alerts']))}
        lookups={{
          people: people.data ?? [],
          suppliers: [],
          /* Infrastructure only. A backup job covers the things that would stop
             the business, and sixty-nine endpoints in a tick list makes the four
             that matter impossible to find. */
          devices: (devices.data ?? [])
            .filter((d) => !['Laptop', 'Desktop', 'Mobile', 'Tablet'].includes(d.type))
            .map((d) => ({ id: d.id, label: d.tag, group: `${d.make} ${d.model}`.trim() })),
          applications: (apps.data ?? []).map((a) => ({ id: a.id, label: a.name, group: a.purpose })),
        }}
        noun="backup job"
        icon="ti-shield-bolt"
        onClose={refresh}
        onSave={(values) => saveJob.mutate(values)}
        onDelete={job ? () => removeJob.mutate(job.id) : undefined}
        saving={saveJob.isPending}
        deleting={removeJob.isPending}
        error={saveJob.error}
      >
        <div className={s.ownedNote}>
          <i className="ti ti-antenna-bars-5" aria-hidden />
          <span>
            Last run and whether it succeeded are not here. Those get pushed in by whatever runs
            the backup (§11), because a green tick somebody typed is worth nothing — it records
            that a person believed it ran, not that it did.
          </span>
        </div>
      </EditDrawer>

      {/* Reading a plan. Deliberately not an editor by default: the moment you
          need this you are not editing it. */}
      <Drawer
        open={plan !== null}
        title={plan?.name ?? ''}
        sub={plan?.trigger}
        avatar={<i className="ti ti-route" aria-hidden />}
        meta={
          plan && (
            <>
              <Badge tone={plan.tier >= 3 ? 'bad' : 'muted'}>Tier {plan.tier}</Badge>
              <Badge tone="info">Target {plan.rto || '—'}</Badge>
            </>
          )
        }
        onClose={() => setPlan(null)}
      >
        {plan && (
          <>
            <ol className={s.steps}>
              {plan.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            <div className={s.actions}>
              <Button icon="ti-pencil" disabled={!online} onClick={() => setPlanDraft({ ...plan })}>
                Edit plan
              </Button>
            </div>
          </>
        )}
      </Drawer>

      {/* Editing a plan. Steps are the plan, so they get a proper list editor. */}
      <Drawer
        open={planDraft !== null}
        title={planDraft?.id ? 'Edit plan' : 'New recovery plan'}
        sub={planDraft?.id ? planDraft.name : undefined}
        avatar={<i className="ti ti-route" aria-hidden />}
        onClose={() => setPlanDraft(null)}
      >
        {planDraft && (
          <>
            <div className={s.field}>
              <label htmlFor="plan-name">Name</label>
              <input
                id="plan-name"
                value={planDraft.name}
                onChange={(e) => setPlanDraft({ ...planDraft, name: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="plan-trigger">Trigger</label>
              <textarea
                id="plan-trigger"
                value={planDraft.trigger}
                placeholder="What has to have happened for somebody to open this."
                onChange={(e) => setPlanDraft({ ...planDraft, trigger: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="plan-rto">Target RTO</label>
              <input
                id="plan-rto"
                value={planDraft.rto}
                placeholder="4 hours"
                onChange={(e) => setPlanDraft({ ...planDraft, rto: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="plan-tier">Tier</label>
              <select
                id="plan-tier"
                value={String(planDraft.tier)}
                onChange={(e) =>
                  setPlanDraft({ ...planDraft, tier: Number(e.target.value) as IRecoveryPlan['tier'] })
                }
              >
                <option value="1">1 — Routine</option>
                <option value="2">2 — Serious</option>
                <option value="3">3 — Major</option>
                <option value="4">4 — Site loss</option>
              </select>
            </div>
            <div className={s.field}>
              <label htmlFor="plan-last">Last rehearsed</label>
              <input
                id="plan-last"
                type="date"
                value={planDraft.last === '—' ? '' : planDraft.last}
                onChange={(e) => setPlanDraft({ ...planDraft, last: e.target.value || '—' })}
              />
            </div>

            <div>
              <div className={s.sectionLabel}>Steps</div>
 <div className={`${s.stack} ${s.rowGap}`} >
                {planDraft.steps.map((step, i) => (
                  <div key={i} className={s.fieldRowBody}>
                    <input
                      value={step}
                      aria-label={`Step ${i + 1}`}
                      placeholder={`Step ${i + 1}`}
                      onChange={(e) =>
                        setPlanDraft({
                          ...planDraft,
                          steps: planDraft.steps.map((x, n) => (n === i ? e.target.value : x)),
                        })
                      }
                    />
                    <button
                      type="button"
                      className={s.iconOnly}
                      aria-label={`Remove step ${i + 1}`}
                      disabled={planDraft.steps.length === 1}
                      onClick={() =>
                        setPlanDraft({
                          ...planDraft,
                          steps: planDraft.steps.filter((_, n) => n !== i),
                        })
                      }
                    >
                      <i className="ti ti-trash" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
              <div className={s.spaced}>
                <Button
                  icon="ti-plus"
                  onClick={() => setPlanDraft({ ...planDraft, steps: [...planDraft.steps, ''] })}
                >
                  Add step
                </Button>
              </div>
            </div>

            <div className={s.actions}>
              <Button
                primary
                icon="ti-device-floppy"
                disabled={!online || savePlan.isPending || planDraft.name.trim() === ''}
                onClick={() =>
                  savePlan.mutate({
                    ...planDraft,
                    steps: planDraft.steps.filter((x) => x.trim() !== ''),
                  })
                }
              >
                {savePlan.isPending ? 'Saving…' : 'Save plan'}
              </Button>
            </div>
          </>
        )}
      </Drawer>

      {/* Logging a restore test — the whole point of the screen. */}
      <Drawer
        open={logging !== null && test !== null}
        title="Log a restore test"
        sub={logging?.name}
        avatar={<i className="ti ti-flask" aria-hidden />}
        onClose={() => {
          setLogging(null);
          setTest(null);
        }}
      >
        {test && (
          <>
            <div className={s.field}>
              <label htmlFor="test-when">When</label>
              <input
                id="test-when"
                type="date"
                value={test.when}
                onChange={(e) => setTest({ ...test, when: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="test-what">What was restored</label>
              <input
                id="test-what"
                value={test.what}
                onChange={(e) => setTest({ ...test, what: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="test-who">Who did it</label>
              <input
                id="test-who"
                value={test.who}
                onChange={(e) => setTest({ ...test, who: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="test-mins">How long, in minutes</label>
              <input
                id="test-mins"
                type="number"
                value={String(test.mins)}
                onChange={(e) => setTest({ ...test, mins: Number(e.target.value || 0) })}
              />
              <div className={s.hint}>
                The number that tells you whether the RTO is real or aspirational.
              </div>
            </div>
            <div className={s.field}>
              <label className={s.checkRow}>
                <input
                  type="checkbox"
                  checked={test.ok}
                  onChange={(e) => setTest({ ...test, ok: e.target.checked })}
                />
                <span>It came back</span>
              </label>
            </div>
            <div className={s.field}>
              <label htmlFor="test-note">Note</label>
              <textarea
                id="test-note"
                value={test.note}
                placeholder="What went wrong, or what you would do differently."
                onChange={(e) => setTest({ ...test, note: e.target.value })}
              />
            </div>

            <div className={s.actions}>
              <Button
                primary
                icon="ti-device-floppy"
                disabled={!online || logTest.isPending || test.what.trim() === ''}
                onClick={() => logTest.mutate(test)}
              >
                {logTest.isPending ? 'Saving…' : 'Log it'}
              </Button>
            </div>

            <div className={s.ownedNote}>
              <i className="ti ti-info-circle" aria-hidden />
              <span>
                A failed test is worth logging too — more so, arguably. It is the difference
                between finding out now and finding out during an incident.
              </span>
            </div>
          </>
        )}
      </Drawer>
    </>
  );
}
